import { Box, useTheme } from "@mui/material";
import clsx from "clsx";
import PropTypes from "prop-types";
import constants from "../../../../../constants/constants.json";

const YieldColumn = ({
  classes,
  color,
  yieldValue,
  DieValue,
  WaferValue,
  taka41aDieValue,
}) => {
  return yieldValue && DieValue ? (
    <Box className={classes.yieldContainer}>
      <Box className={classes.yieldWrapper}>
        <Box
          component={"span"}
          sx={{ color: color }}
          className={classes.yieldContentValue}
        >
          {yieldValue}%
        </Box>{" "}
        <Box
          component={"span"}
          className={clsx(classes.dieValue, classes.themeColor)}
        >
          {taka41aDieValue ? `${DieValue} / ${taka41aDieValue}` : DieValue}{" "}
          {WaferValue && ` (${WaferValue})`}
        </Box>
      </Box>
      <Box className={classes.yieldProgressContainer}>
        <Box
          sx={{
            width: yieldValue > 100 ? "100%" : `${yieldValue}%`,
            background: color,
          }}
          className={classes.yieldProgressBar}
        ></Box>
      </Box>
    </Box>
  ) : (
    <Box sx={{ textAlign: "center" }} className={classes.yieldContainer}>
      <Box
        component={"span"}
        sx={{ color }}
        justifyContent={"center !important"}
        className={classes.yieldWrapper}
      >
        -
      </Box>
    </Box>
  );
};

const YieldColumnsContent = ({
  showHideList,
  classes,
  data,
  selectedTaka = constants.taka31,
}) => {
  const theme = useTheme();
  // Show columns for Taka 4.1 a and Taka 4.1 c
  const showSpecialCols =
    selectedTaka === "Taka 4.1 a" || selectedTaka === "Taka 4.1 c";

  const { mode } = theme.palette;
  const isDarkTheme = mode === "dark";

  const {
    bg_color_2,
    bg_color_3,
    bg_color_4,
    bg_color_5,

    progress_bg_color_1,
    progress_bg_color_2,
    progress_bg_color_3,
    progress_bg_color_4,
  } = theme.lotstatusColorCodes;

  function getColors() {
    return isDarkTheme ? "#FFFFFF" : "#5b5d6e";
  }

  const testYieldConfigs = [
    {
      name: "TestInYield",
      yieldKey: (showSpecialCols) =>
        showSpecialCols ? "TestYield_N1" : "TestYield",
      dieKey: (showSpecialCols) =>
        showSpecialCols ? "TestOutDie_N1" : "TestOutDie",
      show: (showHideList, showSpecialCols, selectedTaka) => showHideList.yield,
      taka41aDieKey: "TestOutDie_N6",
    },
    {
      name: "TestIn1Yield",
      yieldKey: () => "TestYield_S1",
      dieKey: () => "TestOutDie_S1",
      show: (showHideList, showSpecialCols, selectedTaka) =>
        showHideList.yield && showSpecialCols,
      taka41aDieKey: "TestOutDie_S6",
    },
  ];

  const testYieldColumns = testYieldConfigs.map((cfg) => ({
    name: cfg.name,
    className: classes.yieldColumnContainer,
    background: bg_color_5,
    color: progress_bg_color_4,
    show: cfg.show(showHideList, showSpecialCols, selectedTaka),
    render: (data, column) => (
      <YieldColumn
        classes={classes}
        yieldValue={data?.[cfg.yieldKey(showSpecialCols)]}
        DieValue={data?.[cfg.dieKey(showSpecialCols)]}
        color={column.color}
        taka41aDieValue={
          selectedTaka === constants.taka41a
            ? data?.[cfg.taka41aDieKey] || "-"
            : null
        }
      />
    ),
  }));

  const columnConfigs2 = [
    {
      name: "BumpInYield",
      className: classes.yieldColumnContainer,
      background: bg_color_2,
      color: progress_bg_color_1,
      show: showHideList.yield,
      render: (data, column) => (
        <YieldColumn
          classes={classes}
          yieldValue={data?.BumpYield}
          DieValue={data?.BumpOutDie}
          WaferValue={data?.BumpOutWafer}
          color={column.color}
        />
      ),
    },

    {
      name: "ProbeInYield",
      className: classes.yieldColumnContainer,
      background: bg_color_3,
      color: progress_bg_color_2,
      show: showHideList.yield,
      render: (data, column) => (
        <YieldColumn
          classes={classes}
          yieldValue={data?.ProbeYield}
          DieValue={data?.ProbeOutDie}
          WaferValue={data?.ProbeOutWafer}
          color={column.color}
        />
      ),
    },

    {
      name: "AssemblyInYield",
      className: classes.yieldColumnContainer,
      background: bg_color_4,
      color: progress_bg_color_3,
      show: showHideList.yield,
      render: (data, column) => (
        <YieldColumn
          classes={classes}
          yieldValue={
            showSpecialCols ? data?.AssemblyYield_N : data?.AssemblyYield
          }
          DieValue={
            showSpecialCols ? data?.AssemblyOutDie_N : data?.AssemblyOutDie
          }
          color={column.color}
        />
      ),
    },

    {
      name: "Assembly2InYield",
      className: classes.yieldColumnContainer,
      background: bg_color_4,
      color: progress_bg_color_3,
      show: showHideList.yield && showSpecialCols,
      render: (data, column) => (
        <YieldColumn
          classes={classes}
          yieldValue={data?.AssemblyYield_S}
          DieValue={data?.AssemblyOutDie_S}
          color={column.color}
        />
      ),
    },
    ...testYieldColumns,
  ];
  return (
    <>
      {columnConfigs2.map(
        (column, index) =>
          column.show && (
            <Box
              className={column.className}
              data-testid={column.name}
              sx={{
                background: isDarkTheme ? "#1D1F2E" : `${column.background}`,
                borderLeft: index === 0 && `3px solid ${getColors()}`,
                borderRight: `1px solid ${getColors()}`,
              }}
              key={column.name}
            >
              {column.render(data, column)}
            </Box>
          )
      )}
    </>
  );
};

export default YieldColumnsContent;

YieldColumn.propTypes = {
  classes: PropTypes.object.isRequired,
  color: PropTypes.string,
  yieldValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  DieValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  WaferValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  taka41aDieValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

YieldColumnsContent.propTypes = {
  showHideList: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  selectedTaka: PropTypes.string,
};
